import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { products as productsApi, categories as categoriesApi, cart as cartApi, orderMinimums, stockLots, getImageUrl } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, AlertCircle, Loader2, RefreshCw, Tag, Package, ZoomIn, X, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { debounce } from 'lodash';

const DEFAULT_MINIMUM = 3;

// Category-specific minimums (will be loaded from API)
const CATEGORY_MINIMUMS = {
  'caps': 6,
  'bags': 6,
};

const getMinimumForCategory = (category) => {
  return CATEGORY_MINIMUMS[category?.toLowerCase()] || DEFAULT_MINIMUM;
};

const QuickOrderPage = () => {
  usePageTitle('Quick Order');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cart, fetchCart } = useCart();
  const { isAuthenticated } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  
  // Quantities state: { productId: { S: 0, M: 0, L: 0, ... } }
  const [quantities, setQuantities] = useState({});
  const [errors, setErrors] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Image zoom state
  const [zoomProduct, setZoomProduct] = useState(null);
  const [zoomImageIndex, setZoomImageIndex] = useState(0);
  
  // Tab state: 'regular' or 'preorder'
  const [activeTab, setActiveTab] = useState('regular');

  const selectedCategory = searchParams.get('category') || '';
  const selectedCity = searchParams.get('city') || '';

  // City options for filtering
  const cityOptions = [
    { value: 'australia', label: 'Australia' },
    { value: 'melbourne', label: 'Melbourne' },
    { value: 'sydney', label: 'Sydney' },
    { value: 'brisbane', label: 'Brisbane' },
    { value: 'adelaide', label: 'Adelaide' },
    { value: 'perth', label: 'Perth' },
    { value: 'cairns', label: 'Cairns' },
    { value: 'gold coast', label: 'Gold Coast' },
    { value: 'darwin', label: 'Darwin' },
    { value: 'tasmania', label: 'Tasmania' },
    { value: 'bondi', label: 'Bondi' },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to access Quick Order');
      navigate('/login');
      return;
    }
    loadCategories();
    // Fetch cart when page loads to ensure we have latest data
    fetchCart();
  }, [isAuthenticated]);

  useEffect(() => {
    loadProducts();
  }, [selectedCategory]);

  // Initialize quantities from cart when cart or products change
  // But only if we're not currently syncing (to prevent overwriting user input)
  useEffect(() => {
    if (products.length > 0 && cart.items && cart.items.length >= 0 && !isSyncing) {
      initializeQuantitiesFromCart();
    }
  }, [cart.items, products]);

  const loadCategories = async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await productsApi.getAll(params);
      setProducts(response.data.products);
    } catch (error) {
      console.error('Failed to load products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get product sizes based on sizing_type (used during initialization)
  const getSizesForProduct = (product) => {
    if (product.sizing_type === 'onesize') {
      return ['OS'];
    } else if (product.sizing_type === 'ladyfit') {
      return ['S', 'M', 'L', 'XL'];
    } else if (product.sizing_type === 'kids' || product.category === 'kids') {
      return ['1-2', '3-4', '5-6', '7-8', '9-10', '11-12'];
    } else if (product.sizing_type === 'combined') {
      return ['S/M', 'L/XL'];
    }
    return ['S', 'M', 'L', 'XL', 'XXL'];
  };

  const initializeQuantitiesFromCart = () => {
    if (!cart || !cart.items) {
      console.log('Cart not ready yet');
      return;
    }
    
    const newQuantities = {};
    
    // Initialize all products with zero quantities based on their sizing type
    products.forEach(product => {
      const productSizes = getSizesForProduct(product);
      newQuantities[product.id] = {};
      productSizes.forEach(size => {
        newQuantities[product.id][size] = 0;
      });
    });
    
    // Populate from cart
    if (cart.items && cart.items.length > 0) {
      cart.items.forEach(item => {
        if (newQuantities[item.product_id]) {
          Object.entries(item.sizes || {}).forEach(([size, qty]) => {
            newQuantities[item.product_id][size] = qty;
          });
        }
      });
    }
    
    setQuantities(newQuantities);
    setHasUnsavedChanges(false);
    setErrors({});
  };

  const handleCategoryChange = (value) => {
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  const handleCityChange = (value) => {
    if (value === 'all') {
      searchParams.delete('city');
    } else {
      searchParams.set('city', value);
    }
    setSearchParams(searchParams);
  };

  // Debounced sync to cart
  const syncToCart = useCallback(
    debounce(async (productId, newSizes) => {
      setIsSyncing(true);
      try {
        // Build items for sync - only the changed product
        const items = [{
          product_id: productId,
          sizes: newSizes
        }];
        
        await cartApi.sync(items);
        // Don't call fetchCart here - it causes the glitch
        // The cart context will update on next navigation or manual refresh
      } catch (error) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object' && detail.errors) {
          toast.error(detail.errors[0] || 'Sync failed');
        }
      } finally {
        // Small delay before allowing re-initialization
        setTimeout(() => setIsSyncing(false), 300);
      }
    }, 500),
    []
  );

  const handleQuantityChange = (productId, size, value) => {
    const numValue = parseInt(value) || 0;
    const product = products.find(p => p.id === productId);
    const inventory = product?.inventory?.find(i => i.size === size);
    const maxStock = inventory?.quantity || 0;
    const eta = inventory?.eta;
    const category = product?.category || '';
    const minQty = getMinimumForCategory(category);
    const isComingSoon = product?.is_coming_soon;
    
    // Allow pre-order and backorder quantities without stock limit
    const finalValue = (isComingSoon || eta || product?.is_out_of_stock) ? Math.max(0, numValue) : Math.min(Math.max(0, numValue), maxStock);
    
    setQuantities(prev => {
      const newQuantities = {
        ...prev,
        [productId]: {
          ...prev[productId],
          [size]: finalValue
        }
      };
      
      // Sync to cart
      syncToCart(productId, newQuantities[productId]);
      
      return newQuantities;
    });
    
    setHasUnsavedChanges(true);
    
    // Check minimum validation (category-specific)
    if (finalValue > 0 && finalValue < minQty) {
      setErrors(prev => ({
        ...prev,
        [`${productId}-${size}`]: `Min ${minQty}`
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${productId}-${size}`];
        return newErrors;
      });
    }
  };

  const handleRefreshFromCart = () => {
    initializeQuantitiesFromCart();
    toast.success('Quantities refreshed from cart');
  };

  // Calculate totals for summary
  const orderSummary = useMemo(() => {
    let totalItems = 0;
    let totalUnits = 0;
    let totalPrice = 0;
    
    products.forEach(product => {
      // Skip stock lot products from regular cart summary
      if (product.is_stock_lot && product.is_discontinued) return;
      
      const productQty = quantities[product.id] || {};
      const productTotal = Object.values(productQty).reduce((a, b) => a + b, 0);
      
      if (productTotal > 0) {
        totalItems++;
        totalUnits += productTotal;
        // Use sale price if on sale
        const unitPrice = product.is_on_sale ? (product.sale_price || product.price) : product.price;
        totalPrice += productTotal * unitPrice;
      }
    });
    
    return { totalItems, totalUnits, totalPrice };
  }, [quantities, products]);

  // Check if there are any validation errors
  const hasValidationErrors = Object.keys(errors).length > 0;

  const handleGoToCart = async () => {
    // Refresh cart before navigating to ensure accurate data
    await fetchCart();
    navigate('/cart');
  };

  // Size columns including OS for one-size products, kids sizes, and combined sizes
  const adultSizes = ['S', 'M', 'L', 'XL', 'XXL', 'OS', 'S/M', 'L/XL'];
  const kidsSizes = ['1-2', '3-4', '5-6', '7-8', '9-10', '11-12'];
  const combinedSizes = ['S/M', 'L/XL'];

  // Filter products by city if selected
  const cityFilteredProducts = selectedCity 
    ? products.filter(product => 
        product.name.toLowerCase().startsWith(selectedCity.toLowerCase()) ||
        product.name.toLowerCase().includes(selectedCity.toLowerCase())
      )
    : products;
  
  // Split by tab: regular tab shows ALL products, pre-order tab shows only coming soon
  const preOrderProducts = cityFilteredProducts.filter(p => p.is_coming_soon);
  const filteredProducts = activeTab === 'preorder' ? preOrderProducts : cityFilteredProducts;

  // Group products by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'uncategorized';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {});

  // Sort categories alphabetically
  const sortedCategories = Object.keys(groupedProducts).sort();

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#F1F5F9] w-1/4" />
          <div className="h-[400px] bg-[#F1F5F9]" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in" data-testid="quick-order-page">
      {/* Header with Filters on Right */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        {/* Left: Title */}
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A]">Quick Order</h1>
          <p className="text-[#64748B]">
            Select quantities across multiple products - changes sync automatically with your cart
          </p>
        </div>
        
        {/* Right: Filters */}
        <div className="flex flex-row items-center gap-2 sm:gap-3 flex-wrap bg-gray-50 p-3 rounded-lg border border-gray-200">
          <Button 
            variant="outline" 
            onClick={handleRefreshFromCart}
            className="gap-2 h-9 text-xs sm:text-sm bg-white"
            size="sm"
          >
            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Refresh from Cart</span>
            <span className="sm:hidden">Refresh</span>
          </Button>
          <Select value={selectedCity || 'all'} onValueChange={handleCityChange}>
            <SelectTrigger className="w-[130px] sm:w-[150px] h-9 bg-white" data-testid="city-filter">
              <SelectValue placeholder="All Cities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Cities</SelectItem>
              {cityOptions.map((city) => (
                <SelectItem key={city.value} value={city.value}>
                  {city.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedCategory || 'all'} onValueChange={handleCategoryChange}>
            <SelectTrigger className="w-[150px] sm:w-[180px] h-9 bg-white" data-testid="category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.slug}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Quick filter status */}
          {(selectedCity || selectedCategory) && (
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-xs sm:text-sm text-gray-500">
                {filteredProducts.length} items
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  searchParams.delete('city');
                  searchParams.delete('category');
                  setSearchParams(searchParams);
                }}
                className="text-red-600 hover:text-red-700 h-7 px-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex items-center gap-2 mb-6" data-testid="order-type-tabs">
        <button
          onClick={() => setActiveTab('regular')}
          className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors ${
            activeTab === 'regular'
              ? 'bg-[#002FA7] text-white'
              : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
          }`}
          data-testid="tab-regular"
        >
          All Products ({cityFilteredProducts.length})
        </button>
        <button
          onClick={() => setActiveTab('preorder')}
          className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
            activeTab === 'preorder'
              ? 'bg-blue-600 text-white'
              : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
          }`}
          data-testid="tab-preorder"
        >
          <Clock className="w-4 h-4" />
          Pre-Order ({preOrderProducts.length})
        </button>
      </div>

      {/* Pre-Order Info Banner */}
      {activeTab === 'preorder' && preOrderProducts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3 flex items-center gap-3 rounded-lg mb-6">
          <Clock className="w-5 h-5 text-blue-600 shrink-0" />
          <p className="text-sm text-blue-800">
            <strong>Pre-Order:</strong> These products are coming soon. Place your order now to secure your stock when they arrive.
          </p>
        </div>
      )}

      {/* Sync Status & Minimum Order Notice */}
      <div className="space-y-3 mb-6">
        <div className="bg-[#EFF6FF] border border-[#3B82F6] p-3 flex items-center gap-3 rounded-lg">
          <ShoppingCart className="w-5 h-5 text-[#2563EB] shrink-0" />
          <p className="text-sm text-[#1E40AF]">
            <strong>Auto-sync enabled:</strong> Quantities sync automatically with your cart.
          </p>
        </div>
        
        <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3 flex items-center gap-3 rounded-lg">
          <AlertCircle className="w-5 h-5 text-[#D97706] shrink-0" />
          <p className="text-sm text-[#92400E]">
            <strong>Minimum:</strong> {DEFAULT_MINIMUM} units/size (Caps & Bags: 6 units/size)
          </p>
        </div>
      </div>

      {/* No Products Message */}
      {filteredProducts.length === 0 && (
        <div className="bg-white border border-[#E2E8F0] p-8 text-center mb-8">
          <Package className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2">No products found</h3>
          <p className="text-[#64748B] mb-4">
            No products match the selected filters. Try adjusting your filters.
          </p>
          <Button 
            variant="outline"
            onClick={() => {
              searchParams.delete('city');
              searchParams.delete('category');
              setSearchParams(searchParams);
            }}
          >
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Spreadsheet Table - Grouped by Category */}
      <div className="space-y-6 mb-8">
        {sortedCategories.map((category) => {
          const catMinimum = getMinimumForCategory(category);
          // Determine sizes for this category
          const isKidsCategory = category === 'kids';
          const categorySizes = isKidsCategory ? kidsSizes : adultSizes;
          
          return (
          <div key={category} className="bg-white border border-[#E2E8F0] overflow-hidden">
            {/* Category Header */}
            <div className="bg-[#002FA7] text-white px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-semibold capitalize">{category.replace(/-/g, ' ')}</span>
                {catMinimum > DEFAULT_MINIMUM && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                    Min {catMinimum}/size
                  </span>
                )}
                {isKidsCategory && (
                  <span className="text-xs bg-yellow-400/30 px-2 py-0.5 rounded text-yellow-100">
                    Kids Sizes
                  </span>
                )}
              </div>
              <span className="text-sm text-white/80">
                {groupedProducts[category].length} product{groupedProducts[category].length > 1 ? 's' : ''}
              </span>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-[#F8FAFC] sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-[#0F172A] w-[300px]">Product</th>
                    <th className="text-center p-4 text-sm font-semibold text-[#0F172A] w-[80px]">Price</th>
                    {categorySizes.map(size => (
                      <th key={size} className="text-center p-4 text-sm font-semibold text-[#0F172A] w-[90px]">
                        {size === 'OS' ? 'One Size' : size}
                      </th>
                    ))}
                    <th className="text-center p-4 text-sm font-semibold text-[#0F172A] w-[100px]">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {groupedProducts[category].map((product) => {
                    const productQty = quantities[product.id] || {};
                    const productTotal = Object.values(productQty).reduce((a, b) => a + b, 0);
                    const productSizes = isKidsCategory ? kidsSizes : getSizesForProduct(product);
                    // Create inventory map with both quantity and eta
                    const inventoryMap = product.inventory?.reduce((acc, inv) => {
                      acc[inv.size] = { quantity: inv.quantity, eta: inv.eta };
                      return acc;
                    }, {}) || {};
                    
                    // Check if product is in cart - show immediately when quantity is entered OR when in cart
                    const inCart = productTotal > 0 || (cart?.items?.some(item => item.product_id === product.id) || false);
                    
                    // Check if this is a stock lot or on sale
                    const isStockLot = product.is_stock_lot && product.is_discontinued;
                    const isOnSale = product.is_on_sale && !isStockLot;
                    const totalStock = product.inventory?.reduce((acc, inv) => acc + (inv.quantity || 0), 0) || 0;
                    
                    // Sale type labels
                    const saleTypeLabels = {
                      'black_friday': 'BLACK FRIDAY',
                      'boxing_day': 'BOXING DAY',
                      'australia_day': 'AUSTRALIA DAY',
                      'christmas': 'CHRISTMAS',
                      'new_year': 'NEW YEAR',
                      'eofy': 'EOFY',
                      'end_of_season': 'END OF SEASON',
                      'clearance': 'CLEARANCE',
                      'flash_sale': 'FLASH SALE',
                    };
                    
                    // Get sale badge text
                    const getSaleBadgeText = () => {
                      if (product.sale_type === 'custom' && product.sale_type_custom) {
                        return product.sale_type_custom.toUpperCase();
                      }
                      return saleTypeLabels[product.sale_type] || 'SALE';
                    };
                    
                    // Stock lot pricing
                    const stockLotOriginal = isStockLot ? totalStock * product.price : 0;
                    const stockLotDiscount = stockLotOriginal > 0 
                      ? Math.round((1 - (product.stock_lot_price || 0) / stockLotOriginal) * 100) 
                      : 0;
                    
                    // Sale pricing
                    const saleDiscount = isOnSale && product.price > 0 
                      ? Math.round((1 - (product.sale_price || 0) / product.price) * 100) 
                      : 0;

                    // STOCK LOT PRODUCT - Special row rendering
                    if (isStockLot) {
                      return (
                        <tr 
                          key={product.id} 
                          className="bg-orange-50/50 hover:bg-orange-50" 
                          data-testid={`product-row-${product.id}`}
                        >
                          {/* Product Info */}
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-16 h-16 bg-[#F8FAFC] overflow-hidden shrink-0 rounded relative">
                                <img
                                  src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=100'}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute top-0 right-0 bg-orange-500 p-0.5">
                                  <Tag className="w-3 h-3 text-white" />
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-semibold text-[#0F172A] text-sm">{product.name}</p>
                                  <Badge className="bg-orange-500 text-white text-[10px]">STOCK LOT</Badge>
                                </div>
                                <p className="text-xs text-[#64748B] font-mono">{product.sku}</p>
                                <div className="flex gap-1 mt-1">
                                  {product.inventory?.filter(inv => inv.quantity > 0).slice(0, 3).map(inv => (
                                    <span key={inv.size} className="text-[10px] bg-orange-100 text-orange-700 px-1 rounded">
                                      {inv.size}: {inv.quantity}
                                    </span>
                                  ))}
                                  {product.inventory?.filter(inv => inv.quantity > 0).length > 3 && (
                                    <span className="text-[10px] text-orange-600">+more</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Price */}
                          <td className="p-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="text-xs text-[#64748B] line-through">${stockLotOriginal.toFixed(2)}</span>
                              <span className="font-bold text-green-600">${product.stock_lot_price?.toFixed(2)}</span>
                              <Badge className="bg-green-100 text-green-700 text-[10px] mt-1">{stockLotDiscount}% OFF</Badge>
                            </div>
                          </td>

                          {/* Colspan for sizes - show stock lot info */}
                          <td colSpan={categorySizes.length} className="p-4">
                            <div className="flex items-center justify-center gap-4">
                              <div className="text-center">
                                <p className="text-sm text-[#64748B]">Total Units</p>
                                <p className="font-bold text-lg text-orange-600">{totalStock}</p>
                              </div>
                              <Link to={`/products/${product.id}`}>
                                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
                                  <Package className="w-4 h-4" />
                                  Buy Entire Lot
                                </Button>
                              </Link>
                            </div>
                          </td>

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="text-sm text-orange-600 font-medium">Stock Lot</span>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr 
                        key={product.id} 
                        className={`hover:bg-[#F8FAFC] ${inCart ? 'bg-blue-50/30' : ''} ${isOnSale ? 'bg-red-50/20' : ''}`} 
                        data-testid={`product-row-${product.id}`}
                      >
                        {/* Product Info */}
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-16 h-16 bg-gray-100 overflow-hidden shrink-0 rounded relative cursor-pointer group"
                              onClick={() => {
                                setZoomProduct(product);
                                setZoomImageIndex(0);
                              }}
                            >
                              <img
                                src={getImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=100'}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform group-hover:scale-110"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              {isOnSale && (
                                <div className="absolute top-0 right-0 bg-red-500 text-white text-[8px] px-1 font-bold">
                                  {getSaleBadgeText()}
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-[#0F172A] text-sm">{product.name}</p>
                                {inCart && (
                                  <Badge className="bg-[#002FA7] text-white text-xs">In Cart</Badge>
                                )}
                                {product.is_out_of_stock && (
                                  <Badge className="bg-orange-500 text-white text-xs font-bold px-2">BACKORDER</Badge>
                                )}
                                {product.is_new && !product.is_out_of_stock && (
                                  <Badge className="bg-green-500 text-white text-xs font-bold px-2">NEW ARRIVAL</Badge>
                                )}
                                {product.is_coming_soon && (
                                  <Badge className="bg-blue-500 text-white text-xs font-bold px-2">COMING SOON</Badge>
                                )}
                                {isOnSale && (
                                  <Badge className="bg-red-500 text-white text-[10px]">{saleDiscount}% OFF</Badge>
                                )}
                              </div>
                              <p className="text-xs text-[#64748B] font-mono">{product.sku}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {product.sizing_type === 'onesize' ? 'One Size' : product.sizing_type === 'ladyfit' ? 'Lady-fit' : product.sizing_type === 'combined' ? 'Combined' : product.sizing_type === 'kids' ? 'Kids' : 'Unisex'}
                                </Badge>
                                {product.sizing_type === 'combined' && (
                                  <span className="text-xs text-blue-600 font-medium">Only comes in 2 sizes (S/M & L/XL)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Price */}
                        <td className="p-4 text-center">
                          {isOnSale ? (
                            <div className="flex flex-col items-center">
                              <span className="text-xs text-[#64748B] line-through">${product.price?.toFixed(2)}</span>
                              <span className="font-bold text-red-600">${product.sale_price?.toFixed(2)}</span>
                            </div>
                          ) : (
                            <span className="font-semibold text-[#0F172A]">${product.price?.toFixed(2)}</span>
                          )}
                        </td>

                        {/* Size Inputs */}
                        {categorySizes.map(size => {
                          const isValidSize = productSizes.includes(size);
                          const inv = inventoryMap[size] || { quantity: 0, eta: null };
                          const stock = inv.quantity;
                          const eta = inv.eta;
                          const qty = productQty[size] || 0;
                          const hasError = errors[`${product.id}-${size}`];
                          
                          if (!isValidSize) {
                            return (
                              <td key={size} className="p-4 text-center">
                                <span className="text-[#CBD5E1]">—</span>
                        </td>
                      );
                    }

                    return (
                      <td key={size} className="p-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-xs ${
                            product.is_coming_soon ? 'text-blue-600 font-medium' :
                            product.is_out_of_stock ? 'text-orange-500 font-medium' :
                            stock === 0 ? 'text-red-500' : 
                            stock <= 10 ? 'text-orange-500 font-medium' : 
                            'text-[#64748B]'
                          }`}>
                            {product.is_coming_soon ? 'Pre-Order' :
                             product.is_out_of_stock ? 'Backorder' :
                             stock === 0 ? 'Out of stock' : 
                             stock <= 10 ? `Low: ${stock}` : 
                             `${stock} avail`}
                          </span>
                          {stock === 0 && eta && !product.is_coming_soon && (
                            <span className="text-xs text-blue-600">
                              Back: {new Date(eta).toLocaleDateString()}
                            </span>
                          )}
                          <Input
                            type="number"
                            min="0"
                            max={(product.is_out_of_stock || product.is_coming_soon) ? undefined : (stock > 0 ? stock : undefined)}
                            value={qty}
                            onChange={(e) => handleQuantityChange(product.id, size, e.target.value)}
                            disabled={!product.is_out_of_stock && !product.is_coming_soon && stock === 0 && !eta}
                            className={`w-20 text-center h-10 ${
                              hasError ? 'border-red-500 bg-red-50' : ''
                            } ${product.is_coming_soon ? 'bg-blue-50 border-blue-200' : ''} ${
                              !product.is_coming_soon && stock === 0 ? (eta ? 'bg-blue-50 border-blue-200' : 'bg-[#F1F5F9] text-[#94A3B8]') : ''} ${
                              stock > 0 && stock <= 10 && !product.is_coming_soon ? 'border-orange-300 bg-orange-50' : ''
                            } ${
                              qty > 0 ? 'border-[#002FA7] ring-1 ring-[#002FA7]/20' : ''
                            }`}
                            placeholder={product.is_coming_soon ? 'Pre-Order' : (stock === 0 && eta ? 'Backorder' : '')}
                            data-testid={`qty-${product.id}-${size}`}
                          />
                          {hasError && (
                            <span className="text-xs text-red-500">{hasError}</span>
                          )}
                        </div>
                      </td>
                    );
                  })}

                  {/* Row Total */}
                  <td className="p-4 text-center">
                    <div className="flex flex-col items-center">
                      <span className={`font-semibold ${productTotal > 0 ? 'text-[#002FA7]' : 'text-[#0F172A]'}`}>
                        {productTotal}
                      </span>
                      {productTotal > 0 && (
                        <span className="text-xs text-[#64748B]">
                          ${(productTotal * product.price).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
        })}
      </div>

      {/* Order Summary & Go to Cart */}
      <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] p-6 -mx-6 md:-mx-12 lg:-mx-16 shadow-lg">
        <div className="container-wide flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-8">
            <div>
              <span className="text-sm text-[#64748B]">Products:</span>
              <span className="ml-2 font-semibold text-[#0F172A]" data-testid="summary-items">
                {orderSummary.totalItems}
              </span>
            </div>
            <div>
              <span className="text-sm text-[#64748B]">Total Units:</span>
              <span className="ml-2 font-semibold text-[#0F172A]" data-testid="summary-units">
                {orderSummary.totalUnits}
              </span>
            </div>
            <div>
              <span className="text-sm text-[#64748B]">Subtotal:</span>
              <span className="ml-2 text-xl font-bold text-[#002FA7]" data-testid="summary-total">
                ${orderSummary.totalPrice.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {hasValidationErrors && (
              <div className="flex items-center gap-2 text-red-500">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Fix minimum quantities</span>
              </div>
            )}
            
            {syncing && (
              <div className="flex items-center gap-2 text-[#64748B]">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Syncing...</span>
              </div>
            )}
            
            <Button
              onClick={handleGoToCart}
              disabled={orderSummary.totalItems === 0}
              className="bg-[#002FA7] hover:bg-[#0025a0] text-white font-semibold px-8 h-12"
              data-testid="go-to-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Go to Cart ({cart.items.length} items)
            </Button>
          </div>
        </div>
      </div>

      {/* Image Zoom Dialog */}
      <Dialog open={!!zoomProduct} onOpenChange={(open) => !open && setZoomProduct(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-none">
          {zoomProduct && (
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setZoomProduct(null)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              {/* Product name */}
              <div className="absolute top-4 left-4 z-10">
                <h3 className="text-white font-semibold text-lg">{zoomProduct.name}</h3>
                <p className="text-white/70 text-sm">{zoomProduct.sku}</p>
              </div>
              
              {/* Main image with hover zoom */}
              <div className="flex items-center justify-center min-h-[500px] p-8 overflow-hidden group cursor-zoom-in">
                <img
                  src={getImageUrl(zoomProduct.images?.[zoomImageIndex]) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=600'}
                  alt={zoomProduct.name}
                  className="max-h-[70vh] max-w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.6]"
                />
              </div>
              
              {/* Navigation arrows - only if multiple images */}
              {zoomProduct.images?.length > 1 && (
                <>
                  <button
                    onClick={() => setZoomImageIndex(prev => prev === 0 ? zoomProduct.images.length - 1 : prev - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-8 h-8 text-white" />
                  </button>
                  <button
                    onClick={() => setZoomImageIndex(prev => prev === zoomProduct.images.length - 1 ? 0 : prev + 1)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-8 h-8 text-white" />
                  </button>
                </>
              )}
              
              {/* Thumbnail strip - only if multiple images */}
              {zoomProduct.images?.length > 1 && (
                <div className="flex justify-center gap-2 pb-6">
                  {zoomProduct.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setZoomImageIndex(idx)}
                      className={`w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                        idx === zoomImageIndex ? 'border-white' : 'border-transparent opacity-50 hover:opacity-100'
                      }`}
                    >
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* View product link */}
              <div className="absolute bottom-4 right-4">
                <Link
                  to={`/products/${zoomProduct.id}`}
                  className="text-white/70 hover:text-white text-sm underline"
                  onClick={() => setZoomProduct(null)}
                >
                  View Full Product Page →
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickOrderPage;
